import { Fragment, useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { FolderOpenOutlined, SearchOff } from "@mui/icons-material";
import { translate } from "../../../../../translate/translate";
import { buildActivityRows, filterProjects } from "../utils/project.utils";
import { findProjectActivity } from "../utils/activity.utils";
import {
    CELL_BORDER_RADIUS, 
    GRID_BORDER_WIDTH, 
    GRID_GAP, GRID_PADDING, 
    labelCellSx, 
    PHASE_COLUMN_WIDTH, 
    PROJECT_COLUMN_WIDTH, 
    STEP_COLUMN_WIDTH } 
from "../constants/dashboard.constants";
import { NpiProject } from "../types/npi-project.types";
import { NpiDashboardProjectHeaderComponent } from "./npi-dashboard-project-header.component";
import { NpiDashboardTaskStatusCell } from "./npi-dashboard-task-status-cell";

const columnHeadingSx = {
  ...labelCellSx,
  background: "#fff",
  fontSize: "15px",
  fontWeight: 700,
  color: "#83868C",
};

export interface NpiProjectListProps {
  projects: NpiProject[];
  term?: string;
  onProjectDeleted: () => void;
}

export const NpiDashboardProjectList = ({ projects, term = "", onProjectDeleted }: NpiProjectListProps) => {
  const activityRows = useMemo(() => buildActivityRows(projects),
    [projects],
  );

  const columnCount = projects.length + 2;
  const gridMinWidth =
    PHASE_COLUMN_WIDTH +
    STEP_COLUMN_WIDTH +
    projects.length * PROJECT_COLUMN_WIDTH +
    (columnCount - 1) * GRID_GAP +
    2 * (GRID_PADDING + GRID_BORDER_WIDTH);

  if (projects.length === 0) {
    const hasSearch = term.length > 0;
    const EmptyIcon = hasSearch ? SearchOff : FolderOpenOutlined;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          px: 3,
          py: 8,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#F1F5F9",
            mb: 1,
          }}
        >
          <EmptyIcon sx={{ fontSize: 32, color: "#94A3B8" }} />
        </Box>
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#334155" }}>
          {translate.t(hasSearch ? "npi_dashboard_no_results_title" : "npi_dashboard_empty_title")}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#83868C", maxWidth: 420 }}>
          {hasSearch
            ? translate.t("npi_dashboard_no_results_description", { term: term.trim() })
            : translate.t("npi_dashboard_empty_description")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflowX: "auto",
        width: "auto",
      }}
    >
      <Box
        sx={{
          display: "grid",
          minWidth: gridMinWidth,
          boxSizing: "border-box",
          gridTemplateColumns: `${PHASE_COLUMN_WIDTH}px ${STEP_COLUMN_WIDTH}px repeat(${projects.length}, ${PROJECT_COLUMN_WIDTH}px)`,
          gridTemplateRows: "auto",
          gridAutoRows: "65px",
          padding: `${GRID_PADDING}px`,
          paddingBottom: 5,
          height: "max-content",
          gap: `${GRID_GAP}px`,
          borderRadius: `${CELL_BORDER_RADIUS}px`,
          borderWidth: `${GRID_BORDER_WIDTH}px`,
        }}
      >
        <Typography sx={{ ...columnHeadingSx, position: "sticky", left: 0, zIndex: 1, boxShadow: `0 0 0 ${GRID_GAP}px #fff` }}>
          {translate.t("phase").toUpperCase()}
        </Typography>
        <Typography sx={{ ...columnHeadingSx, position: "sticky", left: PHASE_COLUMN_WIDTH + GRID_GAP, zIndex: 1, boxShadow: `0 0 0 ${GRID_GAP}px #fff` }}>
          {translate.t("dashboard_step").toUpperCase()}
        </Typography>

        {projects.map((project) => (
          <NpiDashboardProjectHeaderComponent key={project.id} project={project} onDeleted={onProjectDeleted} />
        ))}

        {activityRows.map((row, index) => {
          const isFirstRowOfPhase =
            index === 0 || activityRows[index - 1].phase !== row.phase;
          const phaseRowSpan = isFirstRowOfPhase
            ? activityRows.filter((item) => item.phase === row.phase)
                .length
            : 0;
          const isFirstRowOfStep =
            !row.subgroup ||
            index === 0 ||
            activityRows[index - 1].phase !== row.phase ||
            activityRows[index - 1].subgroup !== row.subgroup;
          const stepRowSpan = row.subgroup
            ? activityRows.filter((item) => item.phase === row.phase && item.subgroup === row.subgroup).length
            : 1;
          return (
            <Fragment key={row.key}>
              {isFirstRowOfPhase && (
                <Box
                  sx={{
                    ...labelCellSx,
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    boxShadow: `0 0 0 ${GRID_GAP}px #fff`,
                    gridRow: `span ${phaseRowSpan}`,
                    fontWeight: 700,
                  }}
                >
                  {row.phase}
                </Box>
              )}
              {isFirstRowOfStep && (
                <Box sx={{ ...labelCellSx, position: "sticky", left: PHASE_COLUMN_WIDTH + GRID_GAP, zIndex: 1, boxShadow: `0 0 0 ${GRID_GAP}px #fff`, gridColumn: 2, gridRow: `span ${stepRowSpan}`, height: "auto" }}>
                  <Tooltip title={row.step}>
                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.step}
                    </Box>
                  </Tooltip>
                </Box>
              )}
              {projects.map((project) => (
                <NpiDashboardTaskStatusCell
                  key={project.id}
                  activity={findProjectActivity(
                    project,
                    row,
                  )}
                />
              ))}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default NpiDashboardProjectList;
